// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AddToCartButton from "@steiner/App/Components/AddToCartButton/AddToCartButton";
import acceptBidHandler from "@steiner/App/Handlers/OpenBids/AcceptBid";
import declineBid from "@steiner/App/Handlers/OpenBids/DeclineBid";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";

enum fields {
    showPrintAndAddToListOnly = "showPrintAndAddToListOnly",
}

const mapStateToProps = (state: CustomAppState) => ({
    openBidDetailsState: state.xOpenBidDetails,
    openBidsPageUrl: getPageLinkByPageType(state, "OpenBidsPage")?.url,
    cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
    allowMultipleWishLists: getSettingsCollection(state).wishListSettings.allowMultipleWishLists,
});

const mapDispatchToProps = {
    declineBid,
    acceptBidHandler,
    addToWishList,
    setAddToListModalIsOpen,
};

interface OwnProps extends WidgetProps {
    showActionsOnly?: boolean;
    showPrint?: boolean;
    fields: {
        [fields.showPrintAndAddToListOnly]: boolean;
    };
}
type Props = OwnProps &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface OpenBidsDetailsHeaderStyles {
    headerGridContainer?: GridContainerProps;
    title?: TypographyProps;
    titleGridItem?: GridItemProps;
    declineBidButton?: ButtonPresentationProps;
    acceptBidButton?: ButtonPresentationProps;
    printButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    actionWrapper?: InjectableCss;
    printLink?: LinkPresentationProps;
    addAllProductGroupLink?: LinkPresentationProps;

    // xng-changes
    productAddToCartButtonStyles?: ButtonPresentationProps;
    addAllProductGroupButton?: ButtonPresentationProps;
}

export const openBidsDetailsHeaderStyles: OpenBidsDetailsHeaderStyles = {};

const styles = openBidsDetailsHeaderStyles;

const OpenBidsDetailsHeader: FC<Props> = ({
    openBidDetailsState,
    history,
    openBidsPageUrl,
    cartPageUrl,
    allowMultipleWishLists,
    declineBid,
    acceptBidHandler,
    addToWishList,
    setAddToListModalIsOpen,
    showActionsOnly = false,
    showPrint = true,
    fields: { showPrintAndAddToListOnly = false },
    toaster,
}) => {
    if (!openBidDetailsState.dataView || openBidDetailsState.isLoading) {
        return null;
    }
    const toasterContext = useContext(ToasterContext);

    const order = openBidDetailsState.dataView.salesOrder;
    const canAddAllToList = order.lineItemList.lineItem.some((line: any) => line.partIdentifiers.canAddToWishList);

    const declineBidClickHandler = () => {
        declineBid({
            order,
            onSuccess: () => {
                redirectToOpenBids();
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                }
            },
        });
    };

    const acceptBidClickHandler = () => {
        acceptBidHandler({
            generationId: order.orderHeader.generationId,
            orderNumber: order.orderHeader.orderId,
            onSuccess: () => {
                toaster.addToast({
                    body: translate("The requested products have been added to your cart successfully."),
                    messageType: "success",
                });
                if (cartPageUrl) {
                    history.push(cartPageUrl);
                }
            },
            onError: () => {
                toaster.addToast({
                    body: translate("An error occurred while trying to accept bid."),
                    messageType: "danger",
                });
            },
        });
    };

    const redirectToOpenBids = () => {
        if (openBidsPageUrl) {
            history.push(openBidsPageUrl);
        }
    };

    const addAllToListClickHandler = () => {
        const productInfos =
            order.lineItemList.lineItem
                .filter((o: any) => o.partIdentifiers.productId && o.qtyOrdered)
                .map((line: any) => ({
                    productId: line.partIdentifiers.productId!,
                    qtyOrdered: line.qtyOrdered.quantity.text!,
                    unitOfMeasure: line.qtyOrdered.quantity.uom,
                })) || [];
        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps: any) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    const printLabel = translate("Print");

    const declineBidLabel = translate(`Decline Bid`);
    const acceptBidLabel = translate(`Accept Bid`);

    const canBeDeclined = order?.orderHeader?.shippingInformation.shipVia.shipViaId !== "REQUESTED BID";
    const canBeAccepted = order?.orderHeader?.shippingInformation.shipVia.shipViaId !== "REQUESTED BID";
    if (showPrintAndAddToListOnly) {
        return (
            <StyledWrapper {...styles.actionWrapper}>
                <Link {...styles.printLink} onClick={openPrintDialog} aria-label={printLabel}>
                    {printLabel}
                </Link>
                {canAddAllToList && (
                    <Link
                        {...styles.addAllProductGroupLink}
                        onClick={addAllToListClickHandler}
                        aria-label={translate("Add All to Product Group")}
                    >
                        {translate("Add All to Product Group")}
                    </Link>
                )}
            </StyledWrapper>
        );
    }
    return (
        <>
            <GridContainer {...styles.headerGridContainer}>
                {!showActionsOnly ? (
                    <GridItem {...styles.titleGridItem} width={12}>
                        <Typography {...styles.title}>{`${translate("Bid #")} ${
                            openBidDetailsState.erpOrderNumber
                        }`}</Typography>
                    </GridItem>
                ) : (
                    <>
                        <GridItem {...styles.buttonGridItem} className={!showPrint ? "buttons-center" : ""}>
                            {showPrint && (
                                <Link {...styles.printLink} onClick={openPrintDialog} aria-label={printLabel}>
                                    {printLabel}
                                </Link>
                            )}
                            {canBeAccepted && (
                                <AddToCartButton
                                    extendedStyles={styles.productAddToCartButtonStyles}
                                    onClick={acceptBidClickHandler}
                                    aria-label={acceptBidLabel}
                                    label={acceptBidLabel}
                                />
                            )}
                            {canBeDeclined && (
                                <Button
                                    {...styles.declineBidButton}
                                    onClick={declineBidClickHandler}
                                    aria-label={declineBidLabel}
                                >
                                    {declineBidLabel}
                                </Button>
                            )}
                        </GridItem>
                    </>
                )}
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withToaster(OpenBidsDetailsHeader))),
    definition: {
        displayName: "Open Bids Details Header",
        allowedContexts: ["OpenBidsDetailsPage"],
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.showPrintAndAddToListOnly,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
